import {
  ManyIncludedOrderResourceWithFakeRelationshipUnion,
  SingleIncludedOrderResourceWithFakeRelationshipUnion,
} from 'api-client'
import { OrderRequest, OrderResponse } from 'api-order-client'
import axios, { AxiosResponse, CancelToken } from 'axios'

import { OrderFilter, OrderFilters } from '../filters/orders.filters'
import {
  dateFilterToAPIQuery,
  FilterMap,
  mapFiltersToAPIQuery,
} from '../filters/utils'
import { OrderListingModel, OrderModel } from '../model/order.model'
import { OrderRequestWithOptionalMeta } from '../transforms/cart.transforms'
import {
  TransformedListOrder,
  TransformedOrder,
  transformOrder,
  transformOrders,
} from '../transforms/order.transforms'
import { deserialize } from '../utils/deserialize'
import { request } from './request'

/**
 *  Any transforms that need to be done to the filter query
 *  before it gets passed along to the API should go here
 */
const formatFilters = ({
  deliveryDateGreaterEqual,
  deliveryDateLessEqual,
  orderDateGreaterEqual,
  orderDateLessEqual,
  updatedDateGreaterEqual,
  updatedDateLessEqual,
  ...rest
}: FilterMap<OrderFilters>) => ({
  ...rest,
  deliveryDateGreaterEqual: dateFilterToAPIQuery(deliveryDateGreaterEqual),
  deliveryDateLessEqual: dateFilterToAPIQuery(deliveryDateLessEqual),
  orderDateGreaterEqual: dateFilterToAPIQuery(orderDateGreaterEqual),
  orderDateLessEqual: dateFilterToAPIQuery(orderDateLessEqual),
  updatedDateGreaterEqual: dateFilterToAPIQuery(updatedDateGreaterEqual),
  updatedDateLessEqual: dateFilterToAPIQuery(updatedDateLessEqual),
})

interface FetchOrdersParams {
  apiBaseUrl: string
  cancelToken?: CancelToken
  customerContextId?: string
  filters: FilterMap<OrderFilters>
  include?: string
  limit?: number
  offset?: number
  salesGroupId?: string
}

interface FetchOrdersWithAcceptParams extends FetchOrdersParams {
  acceptType: string | undefined
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const extractFilenameFromHeaders = (headers: any) => {
  const matches = /filename="(.*?)"/.exec(headers['content-disposition']) ?? []
  return matches[1]
}

export interface FetchOrdersJsonResponse {
  cancel?: boolean
  error?: boolean
  orders: TransformedListOrder[]
  total: number
}

export interface FetchOrdersCsvResponse {
  cancel?: boolean
  error?: boolean
  fileName: string
  orders: string
}

const FETCH_ORDERS_JSON_ERROR: FetchOrdersJsonResponse = {
  error: true,
  orders: [],
  total: 0,
}

const FETCH_ORDERS_CSV_ERROR: FetchOrdersCsvResponse = {
  error: true,
  fileName: '',
  orders: '',
}

const FETCH_ORDERS_JSON_CANCEL: FetchOrdersJsonResponse = {
  cancel: true,
  orders: [],
  total: 0,
}

const FETCH_ORDERS_CSV_CANCEL: FetchOrdersCsvResponse = {
  cancel: true,
  fileName: '',
  orders: '',
}

async function transformJsonOrders(
  responsePromise: Promise<
    AxiosResponse<ManyIncludedOrderResourceWithFakeRelationshipUnion>
  >,
): Promise<FetchOrdersJsonResponse> {
  try {
    const response = await responsePromise
    const orders = deserialize<OrderListingModel[]>(response?.data)

    return {
      orders: transformOrders(orders) as TransformedListOrder[],
      total: response?.data?.meta?.count | 0,
    }
  } catch (e) {
    return axios.isCancel(e)
      ? FETCH_ORDERS_JSON_CANCEL
      : FETCH_ORDERS_JSON_ERROR
  }
}

async function transformCsvOrders(
  responsePromise: Promise<AxiosResponse<string>>,
): Promise<FetchOrdersCsvResponse> {
  try {
    const response = await responsePromise

    return {
      fileName: extractFilenameFromHeaders(response.headers),
      orders: response.data,
    }
  } catch (e) {
    return axios.isCancel(e) ? FETCH_ORDERS_CSV_CANCEL : FETCH_ORDERS_CSV_ERROR
  }
}

const fetchOrders = async <T>({
  acceptType,
  apiBaseUrl,
  cancelToken,
  customerContextId,
  filters,
  include,
  limit,
  offset,
  salesGroupId,
}: FetchOrdersWithAcceptParams): Promise<AxiosResponse<T>> =>
  request<T>({
    cancelToken,
    headers: {
      Accept: acceptType,
    },
    method: 'GET',
    params: {
      'filter[customerContext.id.equals]': customerContextId,
      'filter[customerContext.salesGroup.id.equals]': salesGroupId,
      include,
      'page[number]': offset,
      'page[size]': limit,
      ...mapFiltersToAPIQuery(formatFilters(filters), OrderFilter),
    },
    url: `${apiBaseUrl}/api/orders`,
  })

export const fetchOrdersCsv = async (
  params: FetchOrdersParams,
): Promise<FetchOrdersCsvResponse> => {
  const responsePromise = fetchOrders<string>({
    ...params,
    acceptType: 'text/csv',
  })
  return transformCsvOrders(responsePromise)
}

export const fetchOrdersJson = async (
  params: FetchOrdersParams,
): Promise<FetchOrdersJsonResponse> => {
  const responsePromise = fetchOrders<ManyIncludedOrderResourceWithFakeRelationshipUnion>(
    { ...params, acceptType: 'application/json' },
  )
  return transformJsonOrders(responsePromise)
}

interface FetchOrderParams {
  apiBaseUrl: string
  customerContextId?: string
  orderId: string
  salesGroupId?: string
}

export const fetchOrderDetails = async ({
  apiBaseUrl,
  customerContextId,
  orderId,
  salesGroupId,
}: FetchOrderParams): Promise<TransformedOrder> => {
  const response = await request<SingleIncludedOrderResourceWithFakeRelationshipUnion>(
    {
      headers: {
        Accept: 'application/json',
      },
      method: 'GET',
      params: {
        'filter[customerContext.id.equals]': customerContextId,
        'filter[customerContext.salesGroup.id.equals]': salesGroupId,
        include:
          'soldTo,shipTo,billTo,customerContext,orderLines.product.customerContextProducts,orderLines.batches,deliveries.batches.orderLine,user,orderLines.product.salesGroupProducts',
      },
      url: `${apiBaseUrl}/api/orders/${orderId}`,
    },
  )

  const deserializedData = deserialize<OrderModel>(response?.data)

  return transformOrder(deserializedData) as TransformedOrder
}

export const validateCart = async (
  orderApiBaseUrl: string,
  order: OrderRequestWithOptionalMeta,
): Promise<OrderResponse | undefined> => {
  const response = await request<OrderResponse>({
    data: order,
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    url: `${orderApiBaseUrl}/api-order/validate`,
  })

  // TODO handle 422 validation error
  /*if (response?.status === 422) {
    // Validation Error
    return (response as HTTPValidationError).detail
  }*/

  return response?.data
}

export const placeOrder = async (
  orderApiBaseUrl: string,
  order: OrderRequest,
) => {
  return await request<OrderResponse>({
    data: order,
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    url: `${orderApiBaseUrl}/api-order/order`,
  })
}
